import { queryClient } from "@api/queryClient";
import {
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";

import {
  VISIT_HISTORY_LIMIT,
  visitsClient,
  type TodayVisit,
  type VisitHistoryCursor,
} from "./visitsClient";

export const visitsKeys = {
  /** Prefix for invalidating every visits query. */
  all: ["visits"] as const,
  today: (sessionKey: string) => ["visits", sessionKey, "today"] as const,
  open: (sessionKey: string) => ["visits", sessionKey, "open"] as const,
  detail: (uuid: string) => ["visits", "detail", uuid] as const,
  forPatient: (sessionKey: string, patientUuid: string) =>
    ["visits", sessionKey, "for-patient", patientUuid] as const,
};

/** Shared options for today's visits, so the suspense rail and a non-suspense
 * reader (e.g. the derived active subject) hit the exact same cache entry. */
const todayVisitsQueryOptions = (sessionKey: string) => ({
  queryKey: visitsKeys.today(sessionKey),
  queryFn: () => visitsClient.today(),
});

export const useTodayVisits = (sessionKey: string) =>
  useSuspenseQuery(todayVisitsQueryOptions(sessionKey));

/**
 * Non-suspense read of the same today's-visits list, gated by `enabled`. Used
 * outside the workspace's AsyncBoundary (useActiveSubject) so a visit lookup
 * never suspends or fetches when the current selection doesn't need it.
 */
export const useTodayVisitsQuery = (sessionKey: string, enabled: boolean) =>
  useQuery({ ...todayVisitsQueryOptions(sessionKey), enabled });

/**
 * Visits from earlier days that were started but never finished: the rail's
 * "Still open" section, and resolving a `?visit=` selection pointing at one (see
 * `findBoardVisit`). Non-suspense on purpose — it must not hold the rail behind a
 * secondary read, and `useActiveSubject` must never suspend. On failure `data` is
 * undefined and the section just doesn't render.
 *
 * Ungated: the rail needs this on every workspace render, so a per-caller
 * `enabled` would save nothing and only mislead about when the fetch happens.
 */
export const useOpenVisits = (sessionKey: string) =>
  useQuery({
    queryKey: visitsKeys.open(sessionKey),
    queryFn: () => visitsClient.open(),
  });

/** A single visit's ordered stages, fetched when a flow card is expanded. */
export const useVisitDetail = (uuid: string) =>
  useSuspenseQuery({
    queryKey: visitsKeys.detail(uuid),
    queryFn: () => visitsClient.get(uuid),
  });

/** A patient's visit pages, for the rail's "Recent visits" view — fetched
 * only once toggled to. A short page means the history is exhausted; a full
 * page hands its last row's (createdAt, id) to the composite cursor. */
export const usePatientVisitHistory = (
  sessionKey: string,
  patientUuid: string,
  enabled: boolean,
) =>
  useInfiniteQuery({
    queryKey: visitsKeys.forPatient(sessionKey, patientUuid),
    queryFn: ({ pageParam }) => visitsClient.forPatient(patientUuid, pageParam),
    initialPageParam: undefined as VisitHistoryCursor | undefined,
    getNextPageParam: (lastPage: TodayVisit[]) => {
      if (lastPage.length < VISIT_HISTORY_LIMIT) return undefined;
      const last = lastPage[lastPage.length - 1];
      // A backend without BE-2116 sends no createdAt — no cursor, one page.
      if (!last.createdAt) return undefined;
      return { before: last.createdAt, beforeUuid: last.id };
    },
    enabled,
  });

/**
 * Warm the flow tracker on hover/focus so it skips its skeleton on expand. The
 * key matches the live read, so the expand hits cache. Fire-and-forget: failures
 * are swallowed and re-surfaced by the real read.
 */
export const prefetchVisitFlow = (uuid: string) =>
  queryClient
    .prefetchQuery({
      queryKey: visitsKeys.detail(uuid),
      queryFn: () => visitsClient.get(uuid),
    })
    .catch(() => {});
